import { useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

export const useRecoverRejectedSalesRequest = () => {
  const [isRecovering, setIsRecovering] = useState(false);
  const profileStore = useProfileStore();

  const canRecoverSalesRequest = async (
    salesRequest: SalesRequest
  ): Promise<{ canRecover: boolean; reason?: string }> => {
    // Only rejected sales requests can be recovered
    if (salesRequest.status !== "rejected") {
      return {
        canRecover: false,
        reason: "La nota de venta no está rechazada",
      };
    }

    // Must have a previous status to recover to
    if (!salesRequest.previousStatus) {
      return {
        canRecover: false,
        reason: "No se puede determinar el estado anterior",
      };
    }

    // Superadmins can always recover
    if (profileStore.role === "superadmin") {
      return { canRecover: true };
    }

    // Get the role of the user who rejected the sales request
    if (!salesRequest.approvedRejectedBy) {
      return {
        canRecover: false,
        reason: "No se puede determinar quién rechazó la nota",
      };
    }

    try {
      const { data: rejectorProfile, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", salesRequest.approvedRejectedBy)
        .single();

      if (error || !rejectorProfile) {
        return {
          canRecover: false,
          reason: "No se pudo obtener información del usuario que rechazó",
        };
      }

      // Only users with the same role as the rejector can recover
      if (profileStore.role !== rejectorProfile.role) {
        return {
          canRecover: false,
          reason: `Solo usuarios con rol ${rejectorProfile.role} pueden recuperar esta nota`,
        };
      }

      return { canRecover: true };
    } catch (error) {
      console.error("Error checking recovery permissions:", error);
      return {
        canRecover: false,
        reason: "Error al verificar permisos de recuperación",
      };
    }
  };

  const recoverSalesRequest = async (
    salesRequest: SalesRequest,
    onSuccess?: () => void
  ): Promise<boolean> => {
    const { canRecover, reason } = await canRecoverSalesRequest(salesRequest);

    if (!canRecover) {
      toast({
        title: "No se puede recuperar",
        description: reason,
        variant: "destructive",
      });
      return false;
    }

    setIsRecovering(true);

    try {
      // Get the old status from the status history to restore the previous status
      const previousStatus = salesRequest.previousStatus!;

      // Update the sales request status back to its previous state
      const { error: updateError } = await supabase
        .from("sales_request")
        .update({
          status: previousStatus,
          approved_rejected_by: null, // Clear the rejection
          rejected_reason: null, // Clear the rejection reason
          updated_at: new Date().toISOString(),
        })
        .eq("id", salesRequest.id);

      if (updateError) throw updateError;

      // Add status history entry for the recovery
      const { error: historyError } = await supabase
        .from("sales_request_status_history")
        .insert({
          sales_request_id: salesRequest.id,
          old_status: "rejected",
          new_status: previousStatus,
          changed_by: profileStore.id,
          changed_at: new Date().toISOString(),
        });

      if (historyError) throw historyError;

      toast({
        title: "Nota recuperada",
        description: `La nota de venta ha sido recuperada al estado: ${previousStatus}`,
        variant: "default",
      });

      if (onSuccess) onSuccess();
      return true;
    } catch (error) {
      console.error("Error recovering sales request:", error);
      toast({
        title: "Error",
        description: "No se pudo recuperar la nota de venta",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsRecovering(false);
    }
  };

  return {
    recoverSalesRequest,
    canRecoverSalesRequest,
    isRecovering,
  };
};
